import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Building2, Search, CheckCircle2, ShieldCheck,
  Paperclip, X, ChevronRight, Upload, Sparkles,
  MapPin, Calendar, Briefcase, Hash, Globe2, BadgeCheck, Lock
} from "lucide-react";
import { getTheme, fonts } from "../theme";
import Navbar from "../components/Navbar";
import { EMPLOYER_CRITERIA } from "../criteria";
import api from "../api";

function Status({ type, msg, theme }) {
  if (!msg) return null;
  const isLight = theme?.key === "light";
  const colors = { 
    error: isLight ? "#DC2626" : "#EF4444", 
    success: isLight ? "#16A34A" : "#22C55E", 
    info: theme?.accent || "#2563EB" 
  };
  const bgColors = {
    error: isLight ? "#FEE2E2" : "rgba(239,68,68,0.12)",
    success: isLight ? "#DCFCE7" : "rgba(34,197,94,0.12)",
    info: isLight ? "#DBEAFE" : "rgba(37,99,235,0.12)"
  };
  return (
    <div className="p-3 rounded-3 mb-3 small fw-semibold" style={{
      background: bgColors[type], border: `1px solid ${colors[type]}44`,
      color: colors[type],
    }}>{msg}</div>
  );
}

const CRITERIA_DESCRIPTIONS = {
  salary: "Timeliness, accuracy & transparency in monthly salary disbursement",
  environment: "Workplace atmosphere, safety, peer collaboration & culture",
  management: "Leadership vision, manager support & clear communication",
  growth: "Learning opportunities, promotions & professional development",
  location_score: "Work-life balance, flexibility, location & commute convenience"
};

const SAMPLE_COMPANIES = [
  { name: "GoldPE Capital", gst: "27ABCDE1234F1Z5" },
  { name: "TechPulse Solutions", gst: "29XYZAB5678C1Z2" },
  { name: "CredTrust Financial", gst: "33DEFGH9012J1Z3" }
];

const EVAL_RANGE_CSS = `
.wibil-eval-range {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 999px;
  outline: none;
  cursor: pointer;
  margin: 0;
}
.wibil-eval-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  border: 2.5px solid currentColor;
  box-shadow: 0 1px 6px rgba(11, 27, 61, 0.2);
  cursor: pointer;
  transition: transform 0.15s ease;
}
.wibil-eval-range::-webkit-slider-thumb:hover {
  transform: scale(1.08);
}
.wibil-eval-range::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border: 2.5px solid currentColor;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 6px rgba(11, 27, 61, 0.2);
  cursor: pointer;
}
.wibil-eval-range:focus-visible {
  outline: none;
}
.wibil-eval-range:focus-visible::-webkit-slider-thumb {
  box-shadow: 0 0 0 4px rgba(2, 132, 199, 0.18);
}
`;

function metricTone(score, isLight) {
  if (score >= 9) {
    return {
      color: isLight ? "#059669" : "#34D399",
      track: isLight ? "#D1FAE5" : "rgba(52, 211, 153, 0.22)",
      chipBg: isLight ? "#ECFDF5" : "rgba(16, 185, 129, 0.12)",
      label: "Excellent"
    };
  }
  if (score >= 7) {
    return {
      color: isLight ? "#0369A1" : "#38BDF8",
      track: isLight ? "#E0F2FE" : "rgba(56, 189, 248, 0.2)",
      chipBg: isLight ? "#F0F9FF" : "rgba(2, 132, 199, 0.14)",
      label: "Strong"
    };
  }
  if (score >= 5) {
    return {
      color: isLight ? "#B45309" : "#FBBF24",
      track: isLight ? "#FEF3C7" : "rgba(251, 191, 36, 0.18)",
      chipBg: isLight ? "#FFFBEB" : "rgba(245, 158, 11, 0.12)",
      label: "Average"
    };
  }
  return {
    color: isLight ? "#DC2626" : "#F87171",
    track: isLight ? "#FEE2E2" : "rgba(248, 113, 113, 0.18)",
    chipBg: isLight ? "#FEF2F2" : "rgba(239, 68, 68, 0.12)",
    label: "Low"
  };
}

function MetricCard({ criterion, score, description, theme, isLight, onChange }) {
  const Icon = criterion.icon;
  const tone = metricTone(score, isLight);
  const fillPct = ((score - 1) / 9) * 100;

  return (
    <div
      className="p-3 p-md-4 rounded-4"
      style={{
        background: isLight ? "#FFFFFF" : "rgba(255,255,255,0.04)",
        border: `1px solid ${isLight ? "#E2E8F0" : theme.line}`,
        boxShadow: isLight ? "0 1px 2px rgba(15, 23, 42, 0.04)" : "none",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease"
      }}
    >
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
        <div className="d-flex align-items-start gap-3 min-w-0">
          <span
            className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: isLight ? "#F0F9FF" : "rgba(2, 132, 199, 0.16)",
              color: theme.accent,
              border: `1px solid ${isLight ? "#E0F2FE" : "rgba(56, 189, 248, 0.2)"}`
            }}
          >
            <Icon size={18} strokeWidth={1.75} />
          </span>
          <div className="min-w-0">
            <div className="fw-semibold" style={{ color: theme.text, fontSize: 15, letterSpacing: "-0.2px" }}>
              {criterion.label}
            </div>
            {description && (
              <div className="mt-1" style={{ fontSize: 12, lineHeight: 1.45, color: theme.textMute }}>
                {description}
              </div>
            )}
          </div>
        </div>
        <div className="text-end flex-shrink-0">
          <div
            className="d-inline-flex align-items-baseline gap-1 px-2 py-1 rounded-3"
            style={{ background: tone.chipBg, color: tone.color }}
          >
            <span style={{ fontFamily: fonts.display, fontSize: 20, fontWeight: 700, lineHeight: 1, letterSpacing: "-0.5px" }}>
              {score}
            </span>
            <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.75 }}>/10</span>
          </div>
          <div className="mt-1" style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: tone.color }}>
            {tone.label}
          </div>
        </div>
      </div>

      <div className="d-flex align-items-center gap-2">
        <span style={{ fontSize: 11, fontWeight: 600, color: theme.textMute, width: 14 }}>1</span>
        <input
          type="range"
          className="wibil-eval-range"
          min="1"
          max="10"
          step="1"
          value={score}
          aria-label={`${criterion.label} rating`}
          onChange={(e) => onChange(Number(e.target.value))}
          style={{
            color: tone.color,
            background: `linear-gradient(to right, ${tone.color} 0%, ${tone.color} ${fillPct}%, ${tone.track} ${fillPct}%, ${tone.track} 100%)`
          }}
        />
        <span style={{ fontSize: 11, fontWeight: 600, color: theme.textMute, width: 18, textAlign: "right" }}>10</span>
      </div>

      <div className="d-flex justify-content-between mt-2 px-1" style={{ marginLeft: 22, marginRight: 26 }}>
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            aria-label={`Set ${criterion.label} to ${n}`}
            className="border-0 p-0"
            style={{
              width: 18,
              height: 18,
              fontSize: 10,
              fontWeight: n === score ? 800 : 500,
              fontFamily: fonts.mono,
              color: n === score ? tone.color : theme.textMute,
              background: "transparent",
              opacity: n === score ? 1 : 0.55,
              cursor: "pointer",
              lineHeight: 1
            }}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function RateEmployer({ user, onLogout, themeMode = "dark", setThemeMode }) {
  const theme = getTheme("rate_employer", themeMode);
  const isLight = theme.key === "light";
  const navigate = useNavigate();

  const [gstNo, setGstNo] = useState("");
  const [searched, setSearched] = useState(false);
  const [company, setCompany] = useState(null);
  const [employerId, setEmployerId] = useState(null);

  const [values, setValues] = useState(() => Object.fromEntries(EMPLOYER_CRITERIA.map(c => [c.key, 5])));
  const [comment, setComment] = useState("");
  const [proof, setProof] = useState(null);
  const [proofType, setProofType] = useState("Appraisal Letter");

  const [error, setError] = useState("");
  const [searchBusy, setSearchBusy] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [done, setDone] = useState(false);

  const runSearch = async (targetGst) => {
    const query = targetGst || gstNo;
    if (!query.trim()) return setError("Please enter a company name or GST number.");
    setSearchBusy(true); setError(""); setCompany(null);
    try {
      const data = await api.post("/verify/gst", { gstNo: query });
      setCompany(data);
      const gst = encodeURIComponent(data.gstNo || data.gstin || query);
      const name = encodeURIComponent(data.companyName || data.legal_name || "");
      const check = await api.get(`/verify/employers/${gst}?name=${name}`);
      setEmployerId(Number(data.employerId || check.employerId || 0) || null);
      setSearched(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSearchBusy(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    runSearch();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!company) return;
    
    if (!employerId && !(company.gstNo && String(company.gstNo).replace(/\s+/g, "").length === 15)) {
      return setError("This employer is not registered on WIBIL. Only GST-verified or registered employers can be rated at this time.");
    }

    setSubmitBusy(true); setError("");
    try {
      const fd = new FormData();
      if (employerId) fd.append("employer_id", String(employerId));
      const gst = String(company.gstNo || company.gstin || "").replace(/\s+/g, "").toUpperCase();
      if (gst) fd.append("gst_no", gst);
      if (company.companyName) fd.append("company_name", company.companyName);
      if (company.address) fd.append("address", company.address);
      Object.entries(values).forEach(([k, v]) => fd.append(k, v));
      fd.append("comment", comment);
      fd.append("proof_type", proofType);
      if (proof) fd.append("proof", proof);

      await api.postForm("/ratings/employer", fd);
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitBusy(false);
    }
  };

  const overallScore = Number(
    (EMPLOYER_CRITERIA.reduce((sum, c) => sum + Number(values[c.key] || 0), 0) / EMPLOYER_CRITERIA.length).toFixed(1)
  );
  const overallTone = metricTone(Math.round(overallScore), isLight);

  return (
    <div style={{ minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: fonts.body, transition: "all 0.2s" }}>
      <style>{EVAL_RANGE_CSS}</style>
      <Navbar theme={theme} userName={user?.name} onLogout={onLogout} themeMode={themeMode} setThemeMode={setThemeMode} />
      
      <div className="container py-4 py-md-5" style={{ maxWidth: 760 }}>
        
        {/* Header Branding */}
        <div className="text-center mb-4">
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-2" style={{ background: theme.badgeBg, color: theme.badgeText, border: `1px solid ${theme.border}`, fontSize: 12, fontWeight: 700 }}>
            <Building2 size={14} color={theme.accent} /> CORPORATE EVALUATION HUB
          </div>
          <h1 className="h3 fw-bold mb-2" style={{ fontFamily: fonts.display, color: theme.text }}>
            Rate an Employer 🏢
          </h1>
            <p className="small mb-0" style={{ color: theme.textMute }}>
              Private workplace evaluation with confidential proof. Comments, scores, and documents stay sealed and are not shown on public or recruiter profiles.
            </p>
        </div>

        {done ? (
          <div className="card p-4 p-md-5 text-center rounded-4 shadow-sm" style={{ background: theme.surface, border: `1px solid ${theme.line}`, color: theme.text }}>
            <div className="d-inline-flex p-3 rounded-circle mx-auto mb-3" style={{ background: "rgba(34,197,94,0.15)", color: "#22C55E" }}>
              <CheckCircle2 size={36} />
            </div>
            <h3 className="h5 fw-bold mb-2" style={{ fontFamily: fonts.display, color: theme.text }}>
              Rating Submitted Successfully 🎉
            </h3>
            <p className="small text-secondary mb-4" style={{ color: theme.textMute, maxWidth: 460, margin: "0 auto" }}>
              Thank you. This rating and proof document are stored as a private confidential record. They are not published on public profiles, recruiter reports, or the workplace score feed.
            </p>
            <div>
              <button 
                type="button" 
                onClick={() => navigate("/employee")} 
                className="btn fw-bold px-4 py-2.5 rounded-3 border-0 shadow-sm"
                style={{ background: theme.accent, color: theme.accentText, fontFamily: fonts.display }}
              >
                Return to Employee Dashboard
              </button>
            </div>
          </div>
        ) : (
          <div className="d-flex flex-column gap-4">
            
            {/* 1. Employer Search Card */}
            <form 
              onSubmit={handleSearchSubmit} 
              className="card p-4 rounded-4 shadow-sm" 
              style={{ 
                background: theme.surface, 
                border: `1px solid ${theme.line}`, 
                color: theme.text,
                boxShadow: isLight ? "0 4px 20px -2px rgba(0,0,0,0.05)" : "none" 
              }}
            >
              <Status type="error" msg={!searched ? error : ""} theme={theme} />
              
              <div className="d-flex align-items-center gap-2 mb-3">
                <span className="p-2 rounded-3" style={{ background: theme.badgeBg, color: theme.accent }}>
                  <Search size={18} />
                </span>
                <div>
                  <h3 className="h6 fw-bold mb-0" style={{ fontFamily: fonts.display, color: theme.text }}>
                    1. Search Employer by GSTIN or Name
                  </h3>
                  <p className="small mb-0" style={{ fontSize: 12, color: theme.textMute }}>
                    Enter company GSTIN or registered business name to fetch verified corporate records.
                  </p>
                </div>
              </div>

              <div className="input-group mb-3">
                <span className="input-group-text bg-body-tertiary" style={{ background: theme.surfaceRaised, borderColor: theme.line, color: theme.textMute }}>
                  <Building2 size={16} />
                </span>
                <input 
                  type="text" 
                  className="form-control" 
                  style={{ background: theme.surfaceRaised, border: `1px solid ${theme.line}`, color: theme.text }}
                  value={gstNo} 
                  onChange={e => setGstNo(e.target.value)} 
                  placeholder="e.g. 27ABCDE1234F1Z5 or GoldPE" 
                  disabled={searchBusy} 
                />
                <button 
                  type="submit" 
                  disabled={searchBusy || !gstNo.trim()} 
                  className="btn fw-bold px-4 border-0 d-inline-flex align-items-center gap-1.5"
                  style={{ background: theme.accent, color: theme.accentText, fontFamily: fonts.display }}
                >
                  <Search size={14} />
                  {searchBusy ? "Searching…" : "Search GST"}
                </button>
              </div>

              {/* Sample Quick Pill Chips */}
              <div className="d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: 12 }}>
                <span style={{ color: theme.textMute, fontWeight: 600 }}>Quick Search:</span>
                {SAMPLE_COMPANIES.map(s => (
                  <button
                    key={s.gst}
                    type="button"
                    onClick={() => {
                      setGstNo(s.gst);
                      runSearch(s.gst);
                    }}
                    className="btn btn-sm px-2.5 py-1 rounded-pill border"
                    style={{
                      background: theme.surfaceRaised,
                      borderColor: theme.line,
                      color: theme.text,
                      fontSize: 11,
                      fontWeight: 600
                    }}
                  >
                    ⚡ {s.name} ({s.gst})
                  </button>
                ))}
              </div>
            </form>

            {/* 2. Rating Form Section */}
            {searched && company && (
              <form 
                onSubmit={submit} 
                className="card p-4 p-md-5 rounded-4 shadow-sm" 
                style={{ 
                  background: theme.surface, 
                  border: `1px solid ${theme.line}`, 
                  color: theme.text,
                  boxShadow: isLight ? "0 4px 20px -2px rgba(0,0,0,0.05)" : "none" 
                }}
              >
                <Status type="error" msg={searched ? error : ""} theme={theme} />
                
                {/* ── Verified Company Details Box ── */}
                <div
                  className="rounded-4 mb-4 overflow-hidden"
                  style={{
                    border: company.verified
                      ? "1.5px solid rgba(22,163,74,0.35)"
                      : `1px solid ${theme.line}`,
                    background: company.verified
                      ? (isLight ? "#f0fdf4" : "rgba(22,163,74,0.06)")
                      : (isLight ? "#F0F7FF" : "rgba(13,110,253,0.08)")
                  }}
                >
                  {/* Header row */}
                  <div
                    className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-4 py-3"
                    style={{
                      borderBottom: `1px solid ${company.verified ? "rgba(22,163,74,0.2)" : theme.line}`,
                      background: company.verified
                        ? (isLight ? "rgba(22,163,74,0.08)" : "rgba(22,163,74,0.1)")
                        : "transparent"
                    }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="p-2 rounded-3 d-flex align-items-center justify-content-center"
                        style={{
                          background: company.verified ? "rgba(22,163,74,0.15)" : "rgba(13,110,253,0.15)",
                          color: company.verified ? "#16a34a" : theme.accent
                        }}
                      >
                        <Building2 size={22} />
                      </div>
                      <div>
                        <h4 className="fw-bold mb-0" style={{ fontSize: 15, color: theme.text, letterSpacing: "-0.3px" }}>
                          {company.companyName}
                        </h4>
                        <div className="d-flex align-items-center gap-2 flex-wrap mt-1">
                          <span
                            className="d-inline-flex align-items-center gap-1 rounded-pill px-2 py-0"
                            style={{
                              background: "rgba(37,99,235,0.1)",
                              color: theme.accent,
                              fontSize: 11,
                              fontWeight: 700,
                              border: "1px solid rgba(37,99,235,0.2)",
                              fontFamily: "monospace"
                            }}
                          >
                            <Hash size={9} /> {company.gstNo}
                          </span>
                          {company.pan && (
                            <span
                              className="d-inline-flex align-items-center gap-1 rounded-pill px-2 py-0"
                              style={{
                                background: "rgba(124,58,237,0.1)",
                                color: "#7c3aed",
                                fontSize: 11,
                                fontWeight: 700,
                                border: "1px solid rgba(124,58,237,0.2)",
                                fontFamily: "monospace"
                              }}
                            >
                              PAN: {company.pan}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="d-flex flex-column align-items-end gap-1">
                      {/* WIBIL registered badge */}
                      {employerId ? (
                        <span
                          className="badge rounded-pill px-3 py-1 fw-bold d-inline-flex align-items-center gap-1"
                          style={{ background: "rgba(22,163,74,0.15)", color: "#16a34a", fontSize: 10, border: "1px solid rgba(22,163,74,0.3)" }}
                        >
                          <CheckCircle2 size={10} /> WIBIL REGISTERED
                        </span>
                      ) : (
                        <span
                          className="badge rounded-pill px-3 py-1 fw-bold"
                          style={{ background: "rgba(239,68,68,0.12)", color: "#dc2626", fontSize: 10, border: "1px solid rgba(239,68,68,0.25)" }}
                        >
                          NOT ON WIBIL
                        </span>
                      )}
                      {/* GST status */}
                      <span
                        className="badge rounded-pill px-3 py-1 fw-bold d-inline-flex align-items-center gap-1"
                        style={{
                          background: company.status === "Active" ? "rgba(22,163,74,0.12)" : "rgba(239,68,68,0.12)",
                          color: company.status === "Active" ? "#16a34a" : "#dc2626",
                          fontSize: 10
                        }}
                      >
                        GST: {company.status || "Active"}
                      </span>
                      {/* SignCare verified badge */}
                      {company.verified && (
                        <span
                          className="d-inline-flex align-items-center gap-1"
                          style={{ fontSize: 10, color: "#16a34a", fontWeight: 700 }}
                        >
                          <ShieldCheck size={10} /> Verified via SignCare
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Details grid */}
                  <div className="px-4 py-3">
                    <div className="row g-2" style={{ fontSize: 12 }}>

                      {/* Address */}
                      <div className="col-12">
                        <div
                          className="d-flex align-items-start gap-2 p-2 rounded-3"
                          style={{ background: isLight ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.04)" }}
                        >
                          <MapPin size={13} color={theme.accent} style={{ marginTop: 1, flexShrink: 0 }} />
                          <div>
                            <div style={{ color: theme.textMute, fontWeight: 600, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.5px" }}>Registered Address</div>
                            <div style={{ color: theme.text, fontWeight: 500 }}>{company.address}</div>
                          </div>
                        </div>
                      </div>

                      {/* Row: Registration Date + Company Type */}
                      {(company.registrationDate || company.companyType) && (
                        <>
                          {company.registrationDate && (
                            <div className="col-6 col-md-4">
                              <div
                                className="p-2 rounded-3 h-100"
                                style={{ background: isLight ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.04)" }}
                              >
                                <div style={{ color: theme.textMute, fontWeight: 600, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                  <Calendar size={9} style={{ marginRight: 3 }} />Reg. Date
                                </div>
                                <div style={{ color: theme.text, fontWeight: 600, marginTop: 2 }}>{company.registrationDate}</div>
                              </div>
                            </div>
                          )}
                          {company.companyType && (
                            <div className="col-6 col-md-4">
                              <div
                                className="p-2 rounded-3 h-100"
                                style={{ background: isLight ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.04)" }}
                              >
                                <div style={{ color: theme.textMute, fontWeight: 600, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                  <Briefcase size={9} style={{ marginRight: 3 }} />Entity Type
                                </div>
                                <div style={{ color: theme.text, fontWeight: 600, marginTop: 2 }}>{company.companyType}</div>
                              </div>
                            </div>
                          )}
                          {company.taxPayerType && (
                            <div className="col-6 col-md-4">
                              <div
                                className="p-2 rounded-3 h-100"
                                style={{ background: isLight ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.04)" }}
                              >
                                <div style={{ color: theme.textMute, fontWeight: 600, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                  <BadgeCheck size={9} style={{ marginRight: 3 }} />Taxpayer Type
                                </div>
                                <div style={{ color: theme.text, fontWeight: 600, marginTop: 2 }}>{company.taxPayerType}</div>
                              </div>
                            </div>
                          )}
                          {company.jurisdiction && (
                            <div className="col-6 col-md-4">
                              <div
                                className="p-2 rounded-3 h-100"
                                style={{ background: isLight ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.04)" }}
                              >
                                <div style={{ color: theme.textMute, fontWeight: 600, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                  <Globe2 size={9} style={{ marginRight: 3 }} />Jurisdiction
                                </div>
                                <div style={{ color: theme.text, fontWeight: 600, marginTop: 2 }}>{company.jurisdiction}</div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Rating Header */}
                <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
                  <div className="d-flex align-items-start gap-2">
                    <span className="p-2 rounded-3" style={{ background: theme.badgeBg, color: theme.accent }}>
                      <Sparkles size={18} />
                    </span>
                    <div>
                      <h3 className="h6 fw-bold mb-0" style={{ fontFamily: fonts.display, color: theme.text }}>
                        2. Workplace Evaluation Metrics
                      </h3>
                      <p className="small mb-0" style={{ fontSize: 12, color: theme.textMute }}>
                        Score each pillar from 1 (poor) to 10 (excellent). Ratings stay private with proof.
                      </p>
                    </div>
                  </div>
                  <div
                    className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-4"
                    style={{
                      background: overallTone.chipBg,
                      border: `1px solid ${overallTone.color}33`
                    }}
                  >
                    <div className="text-end">
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.textMute }}>
                        Overall
                      </div>
                      <div style={{ fontFamily: fonts.display, fontSize: 18, fontWeight: 700, color: overallTone.color, lineHeight: 1.1 }}>
                        {overallScore.toFixed(1)}
                        <span style={{ fontSize: 12, fontWeight: 600, opacity: 0.7 }}> / 10</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rating Sliders */}
                <div className="d-flex flex-column gap-3 mb-4">
                  {EMPLOYER_CRITERIA.map((c) => (
                    <MetricCard
                      key={c.key}
                      criterion={c}
                      score={values[c.key]}
                      description={CRITERIA_DESCRIPTIONS[c.key]}
                      theme={theme}
                      isLight={isLight}
                      onChange={(n) => setValues((prev) => ({ ...prev, [c.key]: n }))}
                    />
                  ))}
                </div>

                {/* Comment Field */}
                <div
                  className="mb-4 p-3 p-md-4 rounded-4"
                  style={{
                    background: isLight ? "#F8FAFC" : "rgba(255,255,255,0.03)",
                    border: `1px solid ${isLight ? "#E2E8F0" : theme.line}`
                  }}
                >
                  <label className="form-label small fw-semibold text-uppercase d-flex align-items-center gap-2 mb-2" style={{ color: theme.textMute, letterSpacing: "0.06em" }}>
                    <Lock size={12} /> Comment / Review
                    <span className="fw-normal text-lowercase" style={{ letterSpacing: 0 }}>(private — not shown publicly)</span>
                  </label>
                  <textarea 
                    rows={3} 
                    className="form-control" 
                    style={{
                      background: isLight ? "#FFFFFF" : theme.surfaceRaised,
                      border: `1px solid ${isLight ? "#E2E8F0" : theme.line}`,
                      color: theme.text,
                      resize: "vertical",
                      borderRadius: 12,
                      fontSize: 14
                    }} 
                    value={comment} 
                    onChange={e => setComment(e.target.value)} 
                    placeholder="Private notes for this confidential record. This comment is not published on profiles or recruiter reports." 
                  />
                </div>

                {/* Proof Type & Proof Upload */}
                <div className="row g-3 mb-4">
                  <div className="col-12 col-md-6">
                    <label className="form-label small fw-semibold text-uppercase" style={{ color: theme.textMute, letterSpacing: "0.06em" }}>
                      Proof Document Type
                    </label>
                    <select
                      className="form-select"
                      style={{
                        background: isLight ? "#FFFFFF" : theme.surfaceRaised,
                        border: `1px solid ${isLight ? "#E2E8F0" : theme.line}`,
                        color: theme.text,
                        borderRadius: 12
                      }}
                      value={proofType}
                      onChange={e => setProofType(e.target.value)}
                    >
                      {["Appraisal Letter", "Performance Review", "Appreciation Letter", "Relieving Certificate", "Other"].map(opt => (
                        <option key={opt} value={opt} style={{ background: theme.surface, color: theme.text }}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label small fw-semibold text-uppercase d-flex align-items-center gap-2" style={{ color: theme.textMute, letterSpacing: "0.06em" }}>
                      <Lock size={12} /> Confidential Proof File
                    </label>
                    {!proof ? (
                      <label 
                        className="form-control d-flex align-items-center gap-2"
                        style={{
                          background: isLight ? "#F8FAFC" : theme.surfaceRaised,
                          border: `1.5px dashed ${isLight ? "#CBD5E1" : theme.line}`,
                          color: theme.textMute,
                          cursor: "pointer",
                          borderRadius: 12,
                          minHeight: 42
                        }}
                      >
                        <Upload size={16} color={theme.accent} />
                        <span className="small text-truncate">Choose PDF, PNG, JPG (max 8MB) — never shown publicly</span>
                        <input 
                          type="file" 
                          accept=".pdf,.png,.jpg,.jpeg,.webp" 
                          className="d-none" 
                          onChange={e => setProof(e.target.files[0] || null)} 
                        />
                      </label>
                    ) : (
                      <div 
                        className="p-2 px-3 rounded-3 d-flex align-items-center justify-content-between border"
                        style={{ background: isLight ? "#F0FDF4" : "rgba(34,197,94,0.1)", borderColor: isLight ? "#86EFAC" : "rgba(34,197,94,0.3)", borderRadius: 12 }}
                      >
                        <div className="d-flex align-items-center gap-2 text-truncate me-2">
                          <Paperclip size={14} className="text-success" />
                          <span className="small fw-semibold text-truncate" style={{ color: theme.text }}>
                            {proof.name}
                          </span>
                        </div>
                        <button 
                          type="button" 
                          onClick={() => setProof(null)} 
                          className="btn btn-sm btn-link text-danger p-0 border-0"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <button 
                  type="submit" 
                  disabled={submitBusy || (!employerId && !(company.gstNo && String(company.gstNo).replace(/\s+/g, "").length === 15))} 
                  className="btn w-100 py-3 fw-bold rounded-3 text-uppercase d-flex align-items-center justify-content-center gap-2 border-0 shadow-sm"
                  style={{
                    background: theme.accent,
                    color: theme.accentText,
                    fontSize: 14,
                    fontFamily: fonts.display
                  }}
                >
                  <ShieldCheck size={16} /> 
                  {submitBusy ? "Submitting Rating…" : "Submit Employer Rating"} 
                  <ChevronRight size={16} />
                </button>
              </form>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

